import React, { memo, forwardRef } from 'react';
import { TextInput } from 'react-native';
import { Box, Input, Text } from '@betty/moonlight';
import Select from '../Select/Select';
import { useInputSelect } from '../InputSelect/hooks/useInputSelect';
import { AddressSearchProps, AddressSuggestion } from './AddressSearch.types';
import { SelectableOptionItem } from '../Select/Select.types';
import { getAddressSelectableOptions } from './utils/getAddressSelectableOptions';

const AddressSearch = forwardRef<TextInput, AddressSearchProps>(
  (
    {
      headerNS,
      headerKey,
      buttonNS,
      buttonKey,
      autoFocus = true,
      allowEmptyField = true,
      maxHeight,
      centerContent,
      onSelect,
      fetchAddresses,
      defaultValue,
      close,
    },
    ref,
  ) => {
    const {
      inputValue,
      filteredSuggestions,
      handleOnFocus,
      handleOnBlur,
      setFilteredSuggestions,
      setInputValue,
    } = useInputSelect([], undefined, false, defaultValue);

    const handleInputChange = async (value: string) => {
      setInputValue(value);

      if (value) {
        const suggestions: AddressSuggestion[] = await fetchAddresses(value);
        const selectableSuggestions = getAddressSelectableOptions(suggestions);
        setFilteredSuggestions(selectableSuggestions);
      } else {
        setFilteredSuggestions([]);
      }
    };

    const handleOptionPress = async (
      selectedValue: SelectableOptionItem | null,
    ) => {
      if (selectedValue?.dynamicValues?.Type === 'Container') {
        const suggestions = await fetchAddresses(
          selectedValue.dynamicValues?.Text,
          selectedValue.dynamicValues?.Id,
        );
        const selectableSuggestions = getAddressSelectableOptions(suggestions);
        setFilteredSuggestions(selectableSuggestions);
      } else {
        setInputValue(selectedValue?.text || '');
      }
    };

    return (
      <Box marginTop="space-300">
        {headerNS && headerKey && (
          <Text
            color="darkSurfacePrimary"
            variant="h5"
            locNS={headerNS}
            locKey={headerKey}
          />
        )}
        <Input
          ref={ref}
          icon="search"
          autoFocus={autoFocus}
          onChangeText={handleInputChange}
          onFocus={handleOnFocus}
          onBlur={handleOnBlur}
          value={inputValue}
          borderRadius="radius-full"
          marginTop="space-300"
        />
        <Select
          selectableOptions={filteredSuggestions}
          centerContent={centerContent}
          allowEmptyField={allowEmptyField}
          maxHeight={maxHeight}
          buttonKey={buttonKey}
          buttonNS={buttonNS}
          onSelect={onSelect}
          onPress={handleOptionPress}
          clearSelectedValue={!inputValue?.trim()}
          close={close}
        />
      </Box>
    );
  },
);

AddressSearch.displayName = 'AddressSearch';

export default memo(AddressSearch);
